import {
  formatEstimateStatus,
  formatSurfaceTotals,
  formatSurfaceType,
  workTypeLabel,
} from "./estimates";
import { formatMeasurementArea, formatMeasurementDimensions } from "./measurementModel";
import { filterVisibleHeaderFields } from "./reportHeader";
import {
  computeEstimateTaxSummary,
  parseEstimateTaxLinesFromTree,
} from "./estimateTax";
import {
  computeEstimateTotals,
  computeLabourLineTotal,
  computeLocationTotal,
  computeMaterialLineTotal,
  computeWorkTypeTotal,
  formatCurrency,
  formatLabourLineDetail,
  getMaterialUnitCost,
} from "./estimateTotals";

export const DEFAULT_ESTIMATE_REPORT_OPTIONS = Object.freeze({
  showMeasurements: true,
  showMaterials: true,
  showLabour: true,
  showPricingDetails: true,
  showEstimateTotal: true,
  showInternalCostBreakdown: false,
});

export const ESTIMATE_REPORT_OPTION_DEFS = [
  {
    key: "showMeasurements",
    label: "Show Measurements",
    future: false,
  },
  {
    key: "showMaterials",
    label: "Show Materials",
    future: false,
  },
  {
    key: "showLabour",
    label: "Show Labour",
    future: false,
  },
  {
    key: "showPricingDetails",
    label: "Show Pricing Details",
    future: false,
  },
  {
    key: "showEstimateTotal",
    label: "Show Estimate Total",
    future: false,
  },
  {
    key: "showInternalCostBreakdown",
    label: "Internal Cost Breakdown",
    future: true,
  },
];

function formatMaterialDetail(row, materialsCatalog, showPricingDetails) {
  const quantity =
    row.quantity != null && row.quantity !== "" ? Number(row.quantity) : null;
  const unitLabel = row.unit_of_measure_snapshot
    ? ` ${row.unit_of_measure_snapshot}`
    : "";

  if (!showPricingDetails) {
    if (quantity == null) return null;
    return `${quantity}${unitLabel}`.trim();
  }

  const unitCost = getMaterialUnitCost(row, materialsCatalog);

  if (quantity == null) {
    return unitCost > 0 ? `${formatCurrency(unitCost)}${unitLabel ? ` /${unitLabel.trim()}` : ""}` : "";
  }

  return `${quantity} × ${formatCurrency(unitCost)}${unitLabel ? ` /${unitLabel.trim()}` : ""}`;
}

function buildMeasurementLines(workType) {
  const lines = [];
  const surfaceTotals = formatSurfaceTotals(workType.surface_totals);

  if (surfaceTotals.length > 0) {
    surfaceTotals.forEach((row) => {
      lines.push(`${row.label}: ${row.sqft.toFixed(2)} sq ft`);
    });
  }

  (workType.sections || []).forEach((section) => {
    const kind = section.record_kind === "deduction" ? "Deduction" : "Area";
    const surface = formatSurfaceType(section.surface_type);
    const dims = `${formatMeasurementDimensions(section)} = ${formatMeasurementArea(section)}`;
    lines.push(`${section.name} (${kind} · ${surface}) — ${dims}`);
  });

  return lines;
}

function buildMaterialLines(workType, materialsCatalog, showPricingDetails) {
  return (workType.materials || []).map((row) => {
    const detail = formatMaterialDetail(
      row,
      materialsCatalog,
      showPricingDetails,
    );
    const lineTotal = computeMaterialLineTotal(row, materialsCatalog);

    return {
      name: row.material_name_snapshot,
      detail: detail || null,
      notes: row.notes?.trim() || null,
      amount: showPricingDetails ? lineTotal : null,
    };
  });
}

function buildLabourLines(workType, showPricingDetails) {
  return (workType.labour || []).map((row) => {
    const lineTotal = computeLabourLineTotal(row, workType);
    const detail = formatLabourLineDetail(row, workType, {
      showPricing: showPricingDetails,
    });

    return {
      name: row.labour_activity,
      detail,
      notes: row.notes?.trim() || null,
      amount: showPricingDetails ? lineTotal : null,
    };
  });
}

export function formatReportDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function buildHeaderPresentation(estimateTree, { clientName, statusLabel } = {}) {
  const resolvedClientName =
    clientName || estimateTree?.clients?.client_name || "—";
  const resolvedProjectName = estimateTree?.title || "Untitled Project";
  const resolvedStatusLabel =
    statusLabel || formatEstimateStatus(estimateTree?.status);
  const resolvedEstimateNumber = estimateTree?.estimate_number || "—";
  const resolvedGeneratedDate = formatReportDate(
    estimateTree?.created_at || estimateTree?.updated_at || new Date(),
  );

  const fields = filterVisibleHeaderFields([
    {
      key: "client",
      label: "Client",
      value: resolvedClientName,
      show: true,
    },
    {
      key: "project",
      label: "Project",
      value: resolvedProjectName,
      show: true,
    },
    {
      key: "estimateNumber",
      label: "Estimate #",
      value: resolvedEstimateNumber,
      show: true,
    },
    {
      key: "date",
      label: "Date",
      value: resolvedGeneratedDate,
      show: true,
    },
    {
      key: "status",
      label: "Status",
      value: resolvedStatusLabel,
      show: true,
    },
  ]);

  return {
    estimateNumber: resolvedEstimateNumber,
    estimateId: estimateTree?.id ?? null,
    clientName: resolvedClientName,
    projectName: resolvedProjectName,
    statusLabel: resolvedStatusLabel,
    generatedDate: resolvedGeneratedDate,
    fields,
  };
}

/**
 * Build a structured report model from estimate data + display options.
 * Used by both Preview and PDF export (single source of truth).
 */
export function buildEstimateReportModel(
  estimateTree,
  materialsCatalog,
  options = DEFAULT_ESTIMATE_REPORT_OPTIONS,
  { clientName, statusLabel } = {},
) {
  const mergedOptions = { ...DEFAULT_ESTIMATE_REPORT_OPTIONS, ...options };

  if (
    options?.showPricing !== undefined &&
    options?.showPricingDetails === undefined
  ) {
    mergedOptions.showPricingDetails = options.showPricing;
    mergedOptions.showEstimateTotal = options.showPricing;
  }

  const financials = computeEstimateTotals(estimateTree, materialsCatalog);
  const preTaxTotal =
    financials.finalEstimatePrice ?? financials.adjustedTotal;
  const storedTaxLines = parseEstimateTaxLinesFromTree(estimateTree);
  const taxSummary = computeEstimateTaxSummary(preTaxTotal, storedTaxLines);
  const estimateTotal = taxSummary.hasTax
    ? taxSummary.grandTotal
    : preTaxTotal;

  const locations = (estimateTree?.locations || []).map((location) => {
    const workTypes = (location.workTypes || []).map((workType) => ({
      id: workType.estimate_feature_id,
      label: workTypeLabel(workType),
      measurements: mergedOptions.showMeasurements
        ? buildMeasurementLines(workType)
        : [],
      materials: mergedOptions.showMaterials
        ? buildMaterialLines(
            workType,
            materialsCatalog,
            mergedOptions.showPricingDetails,
          )
        : [],
      labour: mergedOptions.showLabour
        ? buildLabourLines(workType, mergedOptions.showPricingDetails)
        : [],
      total: mergedOptions.showPricingDetails
        ? computeWorkTypeTotal(workType, materialsCatalog)
        : null,
    }));

    return {
      id: location.room_id,
      name: location.name,
      workTypes,
      total: mergedOptions.showPricingDetails
        ? computeLocationTotal(location, materialsCatalog)
        : null,
    };
  });

  const scopeOverview = locations.map((location) => ({
    name: location.name,
    workTypes: location.workTypes.map((wt) => wt.label),
  }));

  const notes = estimateTree?.notes?.trim() || null;
  const header = buildHeaderPresentation(estimateTree, { clientName, statusLabel });

  return {
    generatedAt: new Date(),
    options: mergedOptions,
    header,
    summary: mergedOptions.showPricingDetails
      ? {
          materialsTotal: financials.calculatedMaterialsTotal,
          labourTotal: financials.calculatedLabourTotal,
          estimateTotal: preTaxTotal,
          hasAdjustments:
            financials.wasteAdjustment !== 0 ||
            financials.travelAdjustment !== 0 ||
            financials.miscAdjustment !== 0,
          wasteAdjustment: financials.wasteAdjustment,
          travelAdjustment: financials.travelAdjustment,
          miscAdjustment: financials.miscAdjustment,
          adjustedTotal: financials.adjustedTotal,
          usesFinalPrice: financials.finalEstimatePrice != null,
          taxableSubtotal: taxSummary.subtotal,
          taxLines: taxSummary.lines,
          totalTax: taxSummary.totalTax,
          grandTotal: taxSummary.grandTotal,
        }
      : null,
    taxSummary: taxSummary.hasTax ? taxSummary : null,
    scopeOverview,
    locations,
    grandTotal: mergedOptions.showEstimateTotal ? estimateTotal : null,
    notes,
  };
}

/**
 * Presentation for the Show Estimate Total section.
 * With taxes: Subtotal → tax lines → Grand Total.
 * Without taxes: Estimate Total only.
 */
export function getEstimateTotalPresentation(reportModel) {
  if (!reportModel?.options?.showEstimateTotal || reportModel.grandTotal == null) {
    return null;
  }

  const taxSummary = reportModel.taxSummary;

  if (taxSummary?.hasTax) {
    return {
      mode: "withTax",
      subtotal: taxSummary.subtotal,
      lines: taxSummary.lines,
      total: taxSummary.grandTotal,
      totalLabel: "Grand Total",
    };
  }

  return {
    mode: "simple",
    total: reportModel.grandTotal,
    totalLabel: reportModel.options?.totalLabel || "Estimate Total",
  };
}

/**
 * Export estimate report as a PDF file (pdfmake + platform-aware delivery).
 */
export { downloadEstimateReportPdf as exportEstimateReportPdf } from "../pdf/estimate/downloadEstimatePdf";

/**
 * Legacy browser print export (deprecated).
 * Prints EstimateReportPrintDocument (off-screen), not the on-screen preview.
 * Disconnected from UI — retained until legacy print stack is removed.
 */
export function exportEstimateReportToPdf() {
  document.body.classList.add("estimate-report-printing");
  window.print();
  window.setTimeout(() => {
    document.body.classList.remove("estimate-report-printing");
  }, 500);
}
